import {
  Create,
  CreateButton,
  Edit,
  EditButton,
  List,
  ShowButton,
  useDrawerForm,
  useTable,
} from '@refinedev/antd';
import {
  Button,
  Checkbox,
  Drawer,
  Form,
  Input,
  InputNumber,
  Radio,
  Space,
  Table,
  Tag,
  TimePicker,
} from 'antd';
import {
  ISittingSchedule,
  SittingRecordStatus,
  SittingScheduleStatus,
} from '../../../interfaces/sitting';
import { ReferenceSelect } from '../../../components/form-item/ReferenceSelect';
import DatePicker from '../../../components/form-item/DatePicker';
import { Link } from 'react-router-dom';
import { LogicalFilter, useNavigation } from '@refinedev/core';
import { DateFilter } from '../../../components/form-item/DateFilter';
import { format, parse } from 'date-fns';

const STATUS: Record<SittingScheduleStatus, { label: string; color: string; disabled?: boolean }> =
  {
    draft: {
      label: '調整中',
      color: 'warning',
    },
    fixed: {
      label: '実施前',
      color: 'default',
    },
    done: {
      label: '添削待ち',
      color: 'processing',
    },
    reported: {
      label: '公開',
      color: 'success',
      disabled: true,
    },
    canceled: {
      label: 'キャンセル',
      color: 'error',
    },
  };

export const SittingScheduleList = () => {
  const { showUrl, show } = useNavigation();

  const { tableProps, filters, setFilters } = useTable({
    syncWithLocation: true,
    resource: 'sittingSchedules',
    filters: {
      initial: [
        {
          field: 'status',
          operator: 'in',
          value: ['draft', 'fixed', 'done'],
        },
      ],
    },
    sorters: {
      initial: [{ field: 'startTimeInt', order: 'desc' }],
    },
    meta: {
      fields: [
        'id',
        'status',
        'date',
        'startTime',
        'startTimeInt',
        'durationHours',
        'memo',
        {
          user: [
            'id',
            'email',
            {
              profile: ['name'],
            },
          ],
          sitter: ['id', 'name'],
          record: ['id', 'status'],
        },
      ],
    },
  });

  // create
  const {
    formProps: createFormProps,
    drawerProps: createDrawerProps,
    show: createDrawerShow,
    saveButtonProps: createSaveButtonProps,
    deleteButtonProps: createDeleteButtonProps,
    id,
    formLoading: editFormLoading,
  } = useDrawerForm<ISittingSchedule>({
    action: 'create',
    syncWithLocation: true,
    meta: {
      fields: [{ sittingSchedule: ['id'] }],
    },
  });

  // edit
  const {
    formProps: editFormProps,
    drawerProps: editDrawerProps,
    show: editDrawerShow,
    saveButtonProps: editSaveButtonProps,
  } = useDrawerForm<ISittingSchedule>({
    action: 'edit',
    meta: {
      fields: [
        'id',
        'userId',
        'sitterId',
        'date',
        'startTime',
        'startTimeInt',
        'durationHours',
        'memo',
        'status',
      ],
    },
    mutationMeta: {
      fields: [{ sittingSchedule: ['id'] }],
    },
  });

  const copyDrawerShow = (schedule: ISittingSchedule) => {
    createDrawerShow();
    createFormProps.form.setFieldsValue({
      userId: schedule.user.id,
      sitterId: schedule.sitter?.id,
      startTimeInt: schedule.startTimeInt,
      startTime: schedule.startTime,
      durationHours: schedule.durationHours,
      memo: schedule.memo,
    });
  };

  return (
    <>
      <List
        headerProps={{ title: 'シッティング予定一覧' }}
        headerButtons={
          <Space wrap>
            <DateFilter currentFilters={filters as LogicalFilter[]} setFilters={setFilters} />
            <Checkbox.Group
              value={
                (filters as LogicalFilter[]).find((filter) => filter.field === 'status')
                  ?.value as string[]
              }
              onChange={(value) => {
                if (value.length === 0) {
                  setFilters(
                    (filters as LogicalFilter[]).filter((filter) => filter.field !== 'status'),
                    'replace',
                  );
                } else {
                  setFilters([{ field: 'status', operator: 'in', value }]);
                }
              }}
              options={Object.keys(STATUS).map((status) => ({
                label: STATUS[status as SittingScheduleStatus].label,
                value: status,
              }))}
            />
            <ReferenceSelect
              resource={'users'}
              optionLabel={'name'}
              label={'ユーザー'}
              allowClear
              style={{ width: 200 }}
              value={
                (filters as LogicalFilter[]).find((filter) => filter.field === 'userId')
                  ?.value as string
              }
              onChange={(value) => setFilters([{ field: 'userId', operator: 'eq', value }])}
              filters={[{ field: 'user_flags.name', operator: 'eq', value: 'use_sitting' }]}
            />
            <ReferenceSelect
              resource={'sitters'}
              optionLabel={'name'}
              label={'シッター'}
              allowClear
              style={{ width: 200 }}
              value={
                (filters as LogicalFilter[]).find((filter) => filter.field === 'sitterId')
                  ?.value as string
              }
              onChange={(value) => {
                setFilters([{ field: 'sitterId', operator: 'eq', value }]);
              }}
            />
            <CreateButton onClick={() => createDrawerShow()} />
          </Space>
        }
      >
        <Table {...tableProps} rowKey="id">
          <Table.Column<ISittingSchedule>
            title="ステータス"
            dataIndex="status"
            render={(value: SittingScheduleStatus) => (
              <Tag color={STATUS[value].color}>{STATUS[value].label}</Tag>
            )}
          />
          <Table.Column title="日付" dataIndex="date" sorter={{ multiple: 0 }} />
          <Table.Column<ISittingSchedule>
            title="開始"
            dataIndex="startTimeInt"
            sorter={{ multiple: 1 }}
            render={(value, record) => record.startTime}
          />
          <Table.Column
            title="ユーザー"
            dataIndex="user"
            render={(value) => (
              <Link to={showUrl('users', value.id)}>
                {value.profile?.name}
                <br />
                {value.email}
              </Link>
            )}
          />
          <Table.Column title="シッティング時間" dataIndex="durationHours" />
          <Table.Column
            title="シッター"
            dataIndex="sitter"
            render={(value) =>
              value ? <Link to={showUrl('sitters', value.id)}>{value.name}</Link> : null
            }
          />
          <Table.Column title="メモ" dataIndex="memo" />
          <Table.Column<ISittingSchedule>
            dataIndex="actions"
            render={(_, record) => {
              return (
                <Space>
                  <EditButton onClick={() => editDrawerShow(record.id)}>変更</EditButton>
                  <Button onClick={() => copyDrawerShow(record)}>コピー</Button>
                  <Button
                    onClick={() => show('sittingSchedules', record.id)}
                    disabled={!record.record}
                  >
                    レポート
                  </Button>
                </Space>
              );
            }}
          />
        </Table>
      </List>

      <Drawer {...createDrawerProps}>
        <Create saveButtonProps={createSaveButtonProps} goBack={false}>
          <Form {...createFormProps} layout="vertical">
            <Form.Item name="userId" label="ユーザー" rules={[{ required: true }]}>
              <ReferenceSelect
                resource="users"
                optionLabel="name"
                label="ユーザー"
                filters={[{ field: 'user_flags.name', operator: 'eq', value: 'use_sitting' }]}
              />
            </Form.Item>
            <Form.Item name="sitterId" label="シッター">
              <ReferenceSelect resource="sitters" optionLabel="name" label="シッター" allowClear />
            </Form.Item>
            <Form.Item
              name="date"
              label="日付"
              getValueFromEvent={(value) => value && format(value, 'yyyy-MM-dd')}
              getValueProps={(value) => ({
                value: value && parse(value, 'yyyy-MM-dd', new Date()),
              })}
              rules={[{ required: true }]}
            >
              <DatePicker />
            </Form.Item>
            <Form.Item
              name="startTime"
              label="開始"
              getValueFromEvent={(value) => value && format(value, 'HH:mm')}
              getValueProps={(value) => ({
                value: value && parse(value.replace(':', ''), 'HHmm', new Date()),
              })}
              rules={[{ required: true }]}
            >
              <DatePicker.TimePicker format="HH:mm" />
            </Form.Item>
            <Form.Item name="durationHours" label="シッティング時間" rules={[{ required: true }]}>
              <InputNumber step={0.5} />
            </Form.Item>
            <Form.Item name="memo" label="メモ">
              <Input.TextArea />
            </Form.Item>

            <Form.Item name="status" label="ステータス">
              <Radio.Group>
                {Object.keys(STATUS).map((status) => (
                  <Radio.Button
                    value={status}
                    disabled={STATUS[status as SittingScheduleStatus].disabled}
                  >
                    {STATUS[status as SittingScheduleStatus].label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>
          </Form>
        </Create>
      </Drawer>
      <Drawer {...editDrawerProps}>
        <Edit
          recordItemId={id}
          saveButtonProps={editSaveButtonProps}
          isLoading={editFormLoading}
          goBack={false}
          breadcrumb={false}
          headerButtons={[]}
        >
          <Form {...editFormProps} layout="vertical">
            <Form.Item name="userId" label="ユーザー" rules={[{ required: true }]}>
              <ReferenceSelect
                resource="users"
                optionLabel="name"
                label="ユーザー"
                filters={[{ field: 'user_flags.name', operator: 'eq', value: 'use_sitting' }]}
              />
            </Form.Item>
            <Form.Item name="sitterId" label="シッター">
              <ReferenceSelect resource="sitters" optionLabel="name" label="シッター" />
            </Form.Item>
            <Form.Item
              name="date"
              label="日付"
              getValueFromEvent={(value) => value && format(value, 'yyyy-MM-dd')}
              getValueProps={(value) => ({
                value: value && parse(value, 'yyyy-MM-dd', new Date()),
              })}
              rules={[{ required: true }]}
            >
              <DatePicker />
            </Form.Item>
            <Form.Item
              name="startTime"
              label="開始"
              getValueFromEvent={(value) => value && format(value, 'HH:mm')}
              getValueProps={(value) => ({
                value: value && parse(value.replace(':', ''), 'HHmm', new Date()),
              })}
              rules={[{ required: true }]}
            >
              <DatePicker.TimePicker format="HH:mm" />
            </Form.Item>
            <Form.Item name="durationHours" label="シッティング時間">
              <InputNumber step={0.5} />
            </Form.Item>
            <Form.Item name="memo" label="メモ">
              <Input.TextArea />
            </Form.Item>
            <Form.Item name="status" label="ステータス" rules={[{ required: true }]}>
              <Radio.Group>
                {Object.keys(STATUS).map((status) => (
                  <Radio.Button
                    value={status}
                    disabled={STATUS[status as SittingScheduleStatus].disabled}
                  >
                    {STATUS[status as SittingScheduleStatus].label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>
          </Form>
        </Edit>
      </Drawer>
    </>
  );
};
